import React, { useContext } from "react";
//External modules
import { useHistory, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
//Contexts
import { useData } from "../../../../../hooks/useData";
import { configStore } from "../../../../../contexts/ConfigContext";
import useDashboard from "../../../../../hooks/useDashboard";
//Components
import { Button } from "@evidenceb/athena-common/design-system/Button";
//Utils
import {
    getModuleById,
    getUrl as getNavigationUrl,
    getResourceIndex,
} from "../../../../../utils/dataRetrieval";
//SCSS
import "./ProgressionNav.scss";
//Interfaces
import {
    ParamTypes,
    ClassroomAnalytics,
} from "../../../../../interfaces/Dashboard";
import LockStatusLabel from "../../../../../components/LockStatusLabel/LockStatusLabel";
import { UserType } from "../../../../../interfaces/User";
import {
    PRLevelIds,
    PRLockStatus,
} from "../../../../../interfaces/PedagogicalResourcesManagement";
import Select from "../../../../../components/Select/Select";
import useClassroom from "../../Classroom/useClassroom";
interface ProgressionNavProps {
    collections: ClassroomAnalytics[];
    collection: ClassroomAnalytics;
    prLockStatus?: PRLockStatus;
}

const ProgressionNav = ({
    collections,
    collection,
    prLockStatus,
}: ProgressionNavProps) => {
    //Contexts
    const data = useData();
    const { config } = useContext(configStore);
    const { data: dashboard } = useDashboard();
    //Hooks
    const history = useHistory();
    const { moduleId, classroomId, groupId } = useParams<ParamTypes>();
    const intl = useIntl();
    const { getUrl } = useClassroom();
    const moduleData = getModuleById(moduleId, data);
    //Functions
    const viewGroups = () => {
        history.push(
            `/${
                getNavigationUrl(
                    config.pages.find((page) => page.type === "DASHBOARD")!,
                    UserType.Teacher
                ) as string
            }/${intl.formatMessage(
                messages.classrooms
            )}/${classroomId}/${moduleId}/${intl.formatMessage(
                messages.groups
            )}`
        );
    };

    const type = classroomId
        ? "classroomProgressionOverview"
        : "groupProgressionOverview";

    const selectCollection = (collectionId: string) => {
        const url = getUrl(type, collectionId, moduleId);
        history.push(url);
    };

    const selectModule = (moduleId: string) => {
        const url = getUrl(type, classroomId ?? groupId, moduleId);
        history.push(url);
    };

    const isClustering = () => {
        if (config.features.clustering) {
            if (
                dashboard?.clustering &&
                dashboard.clustering[classroomId] &&
                dashboard.clustering[classroomId][moduleId] &&
                typeof dashboard.clustering[classroomId][moduleId].error ===
                    "undefined"
            ) {
                return true;
            }
            return false;
        }
        return false;
    };

    return (
        <div className="classrooms-nav">
            <Select
                label={intl.formatMessage(messages.selectClassroom)}
                className="classrooms-nav__select"
                onSelectOption={(collectionId) => {
                    selectCollection(collectionId);
                }}
                selectedOption={collection.name}
            >
                {collections.map(
                    (collection) =>
                        collection.studentsNumber > 0 && (
                            <option
                                key={collection.id}
                                value={
                                    classroomId
                                        ? collection.id
                                        : collection.external_id
                                }
                                disabled={
                                    collection.modulesList.length === 0 ||
                                    collection.modulesList.every(
                                        /** @ts-ignore */
                                        (module) => module.students.length === 0
                                    )
                                }
                            >
                                {intl.formatMessage(messages.classroom)}{" "}
                                {collection.name}
                            </option>
                        )
                )}
            </Select>

            <Select
                label={intl.formatMessage(messages.selectModule)}
                className="classrooms-nav__select"
                onSelectOption={(value) => {
                    selectModule(value);
                }}
                selectedOption={
                    <div className="classrooms-nav__selected-module__wrapper">
                        {`${getResourceIndex(
                            moduleData,
                            data,
                            "module"
                        )}. ${getModuleById(moduleId, data).title.short!}`}
                        {prLockStatus &&
                            prLockStatus[PRLevelIds.ModuleIds].includes(
                                moduleId
                            ) && <LockStatusLabel locked={true} />}
                    </div>
                }
            >
                {collection.modulesList
                    .filter((mod) =>
                        data.modules.some((module) => module.id === mod.id)
                    )
                    .map((module, i) => (
                        <option
                            key={`module-${i}`}
                            value={module.id}
                            disabled={module.progression === 0}
                        >
                            {`
                                ${i + 1} -
                                ${getModuleById(module.id, data).title.short}`}
                            {prLockStatus &&
                                prLockStatus[PRLevelIds.ModuleIds].includes(
                                    module.id
                                ) &&
                                ` (${intl
                                    .formatMessage(messages.locked)
                                    .toLowerCase()})`}
                        </option>
                    ))}
            </Select>

            {classroomId && (
                <>
                    {isClustering() ? (
                        <Button
                            className="classrooms-nav__clustering-link"
                            label={intl.formatMessage(
                                messages.seeGroupSelection
                            )}
                            variant="custom"
                            colors={{
                                background: "var(--element-teacher-default)",
                                text: "var(--text-main-inverted)",
                                hoverBackground:
                                    "var(--element-button-custom-hover)",
                            }}
                            icons={[
                                {
                                    placement: "right",
                                    path: "arrow_right",
                                },
                            ]}
                            onClick={viewGroups}
                        />
                    ) : (
                        <Button
                            className="classrooms-nav__clustering-link --disabled"
                            label={intl.formatMessage(messages.noCluster)}
                            disabled={true}
                            icons={[
                                {
                                    placement: "right",
                                    path: "arrow_right",
                                },
                            ]}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default ProgressionNav;

const messages = defineMessages({
    classroom: {
        id: "dashboard-common-class",
        defaultMessage: "Class",
    },
    classrooms: {
        id: "dashboard-paths-classes",
        defaultMessage: "classes",
    },
    groups: {
        id: "dashboard-paths-groups",
        defaultMessage: "groups",
    },
    progression: {
        id: "dashboard-paths-progression",
        defaultMessage: "progression",
    },
    selectClassroom: {
        id: "dashboard-common-select-classroom",
        defaultMessage: "Select a classroom",
    },
    selectModule: {
        id: "dashboard-common-select-module",
        defaultMessage: "Select a module",
    },
    locked: {
        id: "prm-manager-listItem-deactivated",
        defaultMessage: "locked",
    },
    seeGroupSelection: {
        id: "dashboard-progressionOverview-seeGroupsOfSelection",
        defaultMessage: "Show groups of selection",
    },
    noCluster: {
        id: "dashboard-progressionOverview-noClusteringGenerated",
        defaultMessage: "No clusters were generated",
    },
});
